// frontend/src/pages/admin/Products.js
import React, { useState, useEffect, useCallback } from 'react';
// Removed 'message' import, now using global notification system
import { Table, Button, Space, Input, Popconfirm, Badge, Select, Tooltip, Modal } from 'antd'; // Added Modal for fallback error
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
  CheckCircleOutlined, CloseCircleOutlined, MailOutlined, SyncOutlined // Added SyncOutlined for restore
} from '@ant-design/icons';
import api from '../../utils/api';
import './users.css'; // Consider renaming this to 'admin-common.css' or 'products.css'
import ProductForm from './ProductForm';
import { useNotificationApi } from '../../context/NotificationContext'; // <--- NEW IMPORT

const { Search } = Input;
const { Option } = Select;

const Products = ({ productType }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [filterCategory, setFilterCategory] = useState('all');
  const [productCategories, setProductCategories] = useState([]);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [sortBy, setSortBy] = useState('createdAt:DESC,id:DESC');

  // --- NEW: States for Popconfirm loading indicators ---
  const [deactivatingId, setDeactivatingId] = useState(null);
  const [activatingId, setActivatingId] = useState(null);
  const [softDeletingId, setSoftDeletingId] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [hardDeletingId, setHardDeletingId] = useState(null);
  // --- END NEW ---

  const notificationApi = useNotificationApi(); // <--- NEW: Get notification API from context

  // Helper function to show errors (persistent notification, top-right)
  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  // Helper function to show success (transient notification, top-right)
  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  // Helper function to show loading (sticky notification, top-right, with delay and min display time)
  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300;
    const minDisplayTime = 500;

    const startTime = Date.now();

    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        if (dismissNotification) {
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

// Fetch all product categories for the filter dropdown (and to pass to ProductForm)
const fetchProductCategoriesForFilter = useCallback(async () => {
  const loadingKey = 'fetchProductCategoriesForFilterLoading';
  const hideLoading = showLoading(loadingKey, 'Loading categories for filter...');
  try {
    // Fetch all categories, including inactive/deleted, so they can be selected in the filter
    const response = await api.get('/api/product-categories', { params: { status: 'all' } });
    setProductCategories(response.data.data || []);
    console.log('Frontend: Fetched product categories for filter:', response.data.data);
    hideLoading();
  } catch (error) {
    hideLoading();
    console.error('Error fetching product categories for filter:', error.response?.data || error.message);
    showError('Failed to Load Categories', 'Could not load product categories for the filter dropdown.', 'fetchProductCategoriesForFilterError');
  }
}, [showLoading, showError]);

// Function to fetch all products from the backend (updated without pagination)
const fetchProducts = useCallback(async (currentSortBy = 'createdAt:DESC,id:DESC', triggeredByAction = false) => {
  setLoading(true);
  const loadingKey = 'fetchProductsLoading';
  const hideLoading = showLoading(loadingKey, 'Loading products...');

  try {
    // Ensure sortBy is always a string with default value
    const safeSortBy = typeof currentSortBy === 'string' ? currentSortBy : 'createdAt:DESC,id:DESC';
    
    const params = {
      search: searchText,
      status: filterStatus,
      sortBy: safeSortBy,
    };

    if (filterCategory !== 'all') {
      params.productCategoryId = filterCategory;
    }

    // 🆕 Client Computing Portal — tab-based filter
    // productType="euc"        → show only EUC products
    // productType="enterprise" → show only non-EUC products
    if (productType === 'euc') {
      params.productCategoryCode = 'EUC';
    } else if (productType === 'enterprise') {
      params.excludeEUC = 'true';
    }

    console.log('Frontend: Fetching products with params:', params);
    const response = await api.get('/api/products', { params });
    let fetchedProducts = response.data.data || [];

    if (!Array.isArray(fetchedProducts)) {
      console.warn('Products.js: API response for products was not an array or expected object format. Assuming empty array.');
      fetchedProducts = [];
    }
    console.log('Products.js: Fetched products:', fetchedProducts);
    setProducts(fetchedProducts);
    setSortBy(safeSortBy);
    hideLoading();

    if (!triggeredByAction) {
      showSuccess('Products Loaded', 'Product list fetched successfully!', 'fetchProductsSuccess');
    }
  } catch (error) {
    hideLoading();
    console.error('Error fetching products:', error.response?.data || error.message, error);
    showError('Failed to Fetch Products', error.response?.data?.message || 'An unexpected error occurred while fetching products.', 'fetchProductsError');
  } finally {
    setLoading(false);
  }
}, [searchText, filterStatus, filterCategory, sortBy, productType, showLoading, showSuccess, showError]);

// Effect to fetch initial data on component mount
useEffect(() => {
  fetchProductCategoriesForFilter();
  fetchProducts();
}, [fetchProducts, fetchProductCategoriesForFilter]);

  // Handler for Ant Design Table changes (pagination, sorting)
  const handleTableChange = (newPagination, filters, sorter) => {
    let newSortBy = '';
    if (Array.isArray(sorter) && sorter.length > 0) {
      newSortBy = sorter.map(s => {
        const field = s.field;
        const order = s.order === 'ascend' ? 'ASC' : 'DESC';
        return `${field}:${order}`;
      }).join(',');
    } else if (sorter.field && sorter.order) {
      const field = sorter.field;
      const order = sorter.order === 'ascend' ? 'ASC' : 'DESC';
      newSortBy = `${field}:${order}`;
    } else {
      newSortBy = 'createdAt:DESC,id:DESC';
    }

    // Update pagination state first, then fetch products
    setPagination(newPagination);
    setSortBy(newSortBy);
    fetchProducts(newPagination, newSortBy, true); // Triggered by action
  };

  // Handler for search input
  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchProducts({ ...pagination, current: 1 }, sortBy, true); // Triggered by action
  };

  // Handler for filter by status
  const handleFilterStatusChange = (value) => {
    setFilterStatus(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchProducts({ ...pagination, current: 1 }, sortBy, true); // Triggered by action
  };

  // Handler for filter by category
  const handleFilterCategoryChange = (value) => {
    setFilterCategory(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchProducts({ ...pagination, current: 1 }, sortBy, true); // Triggered by action
  };

  // Handler for adding a new product
  const handleAddProduct = () => {
    setEditingProduct(null);
    setIsModalVisible(true);
  };

  // Handler for editing an existing product
  const handleEditProduct = (productRecord) => {
    console.log('Products.js: handleEditProduct triggered. Product record received:', productRecord);
    setEditingProduct(productRecord);
    setIsModalVisible(true);
  };

  // Handler for deactivating a product
  const handleDeactivateProduct = async (productId, productName) => {
    setDeactivatingId(productId);
    const loadingKey = `productStatusUpdate-${productId}`;
    const hideLoading = showLoading(loadingKey, `Deactivating ${productName}...`);

    try {
      await api.patch(`/api/products/${productId}/deactivate`);
      hideLoading();
      showSuccess('Product Status Updated', `${productName} was deactivated.`, 'productStatusUpdateSuccess');
      fetchProducts(pagination, sortBy, true);
    } catch (error) {
      hideLoading();
      console.error('Error deactivating product:', error.response?.data || error.message, error);
      showError('Cannot Deactivate Product', error.response?.data?.message || `Failed to deactivate ${productName}.`, `deactivateProductError-${productId}`);
    } finally {
      setDeactivatingId(null);
    }
  };

  // Handler for activating a product
  const handleActivateProduct = async (productId, productName) => {
    setActivatingId(productId);
    const loadingKey = `productStatusUpdate-${productId}`;
    const hideLoading = showLoading(loadingKey, `Activating ${productName}...`);

    try {
      await api.patch(`/api/products/${productId}/activate`);
      hideLoading();
      showSuccess('Product Status Updated', `${productName} was activated.`, 'productStatusUpdateSuccess');
      fetchProducts(pagination, sortBy, true);
    } catch (error) {
      hideLoading();
      console.error('Error activating product:', error.response?.data || error.message, error);
      showError('Cannot Activate Product', error.response?.data?.message || `Failed to activate ${productName}.`, `activateProductError-${productId}`);
    } finally {
      setActivatingId(null);
    }
  };

  // Handler for soft deleting a product
  const handleSoftDeleteProduct = async (productId, productName) => {
    setSoftDeletingId(productId);
    const loadingKey = `productSoftDelete-${productId}`;
    const hideLoading = showLoading(loadingKey, `Soft deleting ${productName}...`);

    try {
      await api.patch(`/api/products/${productId}/soft-delete`);
      hideLoading();
      showSuccess('Product Soft Deleted', `Product "${productName}" soft deleted successfully!`, 'productSoftDeleteSuccess');
      fetchProducts(pagination, sortBy, true);
    } catch (error) {
      hideLoading();
      console.error('Error soft deleting product:', error.response?.data || error.message, error);
      showError('Cannot Soft Delete Product', error.response?.data?.message || `Failed to soft delete product "${productName}".`, `softDeleteProductError-${productId}`);
    } finally {
      setSoftDeletingId(null);
    }
  };

  // Handler for restoring a soft-deleted product
  const handleRestoreProduct = async (productId, productName) => {
    setRestoringId(productId);
    const loadingKey = `productRestore-${productId}`;
    const hideLoading = showLoading(loadingKey, `Restoring ${productName}...`);

    try {
      await api.patch(`/api/products/${productId}/restore`);
      hideLoading();
      showSuccess('Product Restored', `Product "${productName}" restored successfully!`, 'productRestoreSuccess');
      fetchProducts(pagination, sortBy, true);
    } catch (error) {
      hideLoading();
      console.error('Error restoring product:', error.response?.data || error.message, error);
      showError('Cannot Restore Product', error.response?.data?.message || `Failed to restore product "${productName}".`, `restoreProductError-${productId}`);
    } finally {
      setRestoringId(null);
    }
  };

  // Handler for hard deleting a product (if not linked)
  const handleHardDeleteProduct = async (productId, productName) => {
    setHardDeletingId(productId);
    const loadingKey = `productHardDelete-${productId}`;
    const hideLoading = showLoading(loadingKey, `Permanently deleting ${productName}...`);

    try {
      await api.delete(`/api/products/${productId}`);
      hideLoading();
      showSuccess('Product Deleted', `Product "${productName}" permanently deleted successfully!`, 'productHardDeleteSuccess');
      fetchProducts(pagination, sortBy, true);
    } catch (error) {
      hideLoading();
      console.error('Error hard deleting product:', error.response?.data || error.message, error);
      showError('Cannot Delete Product', error.response?.data?.message || `Failed to hard delete product "${productName}".`, `hardDeleteProductError-${productId}`);
    } finally {
      setHardDeletingId(null);
    }
  };


  // Define columns for Ant Design Table
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: true,
      defaultSortOrder: sortBy.includes('id:ASC') ? 'ascend' : (sortBy.includes('id:DESC') ? 'descend' : undefined),
      width: '6%',
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      sorter: true,
      defaultSortOrder: sortBy.includes('code:ASC') ? 'ascend' : (sortBy.includes('code:DESC') ? 'descend' : undefined),
      width: '10%',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      defaultSortOrder: sortBy.includes('name:ASC') ? 'ascend' : (sortBy.includes('name:DESC') ? 'descend' : undefined),
      width: '15%',
      render: (text, record) => ( // Render as clickable link for editing
        <a onClick={() => handleEditProduct(record)}>{text}</a>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text) => text || 'N/A',
      width: '15%',
    },
    {
      title: 'Category',
      dataIndex: 'productCategoryName',
      key: 'productCategoryName',
      sorter: true,
      defaultSortOrder: sortBy.includes('productCategoryName:ASC') ? 'ascend' : (sortBy.includes('productCategoryName:DESC') ? 'descend' : undefined),
      width: '12%',
    },
    {
      title: 'Notification Group',
      dataIndex: 'notificationGroupName',
      key: 'notificationGroupName',
      sorter: true,
      defaultSortOrder: sortBy.includes('notificationGroupName:ASC') ? 'ascend' : (sortBy.includes('notificationGroupName:DESC') ? 'descend' : undefined),
      render: (text, record) => (
        record.notificationGroupName ? (
          <Tooltip title={record.notificationGroupEmail}>
            <Space>
              <MailOutlined />
              {record.notificationGroupName}
            </Space>
          </Tooltip>
        ) : (
          '-'
        )
      ),
      width: '12%',
    },
    {
      title: 'Sort Order',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      sorter: true,
      defaultSortOrder: sortBy.includes('sortOrder:ASC') ? 'ascend' : (sortBy.includes('sortOrder:DESC') ? 'descend' : undefined),
      width: '8%',
      align: 'center',
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive, record) => (
        <>
          {record.deletedAt ? (
            <Badge status="default" text="Deleted" />
          ) : (
            <Badge
              status={isActive ? 'success' : 'error'}
              text={isActive ? 'Active' : 'Inactive'}
            />
          )}
        </>
      ),
      width: '8%',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => text ? new Date(text).toLocaleString() : 'N/A',
      sorter: true,
      defaultSortOrder: sortBy.includes('createdAt:ASC') ? 'ascend' : (sortBy.includes('createdAt:DESC') ? 'descend' : undefined),
      width: '12%',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '16%',
      render: (_, record) => (
        <Space size="middle">
          {/* Edit button */}
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditProduct(record)}
            aria-label={`Edit ${record.name}`}
            disabled={record.deletedAt}
          />

          {/* Activate/Deactivate/Restore/Soft Delete/Hard Delete Logic (Standardized) */}
          {record.deletedAt ? (
            // If deleted, only offer Restore and Hard Delete
            <>
              <Popconfirm
                title={`Are you sure you want to restore "${record.name}"?`}
                onConfirm={() => handleRestoreProduct(record.id, record.name)}
                okText="Yes"
                cancelText="No"
                okButtonProps={{ loading: restoringId === record.id }}
              >
                <Button type="link" icon={<SyncOutlined />} aria-label={`Restore ${record.name}`} />
              </Popconfirm>
              <Popconfirm
                title={`Are you absolutely sure you want to PERMANENTLY DELETE the product "${record.name}"? This cannot be undone and will fail if contracts or tickets are linked.`}
                onConfirm={() => handleHardDeleteProduct(record.id, record.name)}
                okText="Yes, Delete Permanently"
                cancelText="No, Keep It"
                okButtonProps={{ danger: true, loading: hardDeletingId === record.id }}
              >
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  aria-label={`Hard Delete ${record.name}`}
                />
              </Popconfirm>
            </>
          ) : (
            // If not deleted, offer Activate/Deactivate and Soft Delete
            <>
              {record.isActive ? (
                <Popconfirm
                  title={`Are you sure you want to deactivate "${record.name}"?`}
                  onConfirm={() => handleDeactivateProduct(record.id, record.name)}
                  okText="Yes"
                  cancelText="No"
                  okButtonProps={{ loading: deactivatingId === record.id, danger: true }}
                >
                  <Button type="link" danger icon={<CloseCircleOutlined />} aria-label={`Deactivate ${record.name}`} />
                </Popconfirm>
              ) : (
                <Popconfirm
                  title={`Are you sure you want to activate "${record.name}"?`}
                  onConfirm={() => handleActivateProduct(record.id, record.name)}
                  okText="Yes"
                  cancelText="No"
                  okButtonProps={{ loading: activatingId === record.id }}
                >
                  <Button type="link" icon={<CheckCircleOutlined />} aria-label={`Activate ${record.name}`} />
                </Popconfirm>
              )}
              <Popconfirm
                title={`Are you sure you want to soft delete "${record.name}"? This will also deactivate it.`}
                onConfirm={() => handleSoftDeleteProduct(record.id, record.name)}
                okText="Yes, Soft Delete"
                cancelText="No"
                okButtonProps={{ danger: true, loading: softDeletingId === record.id }}
              >
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  aria-label={`Soft Delete ${record.name}`}
                />
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

return (
  <div className="products-page">
    <div className="products-header">
      <h2>
        {productType === 'euc'
          ? 'EUC Product Management'
          : productType === 'enterprise'
          ? 'Enterprise Product Management'
          : 'Product Management'}
      </h2>
      <div className="products-actions">
        <Space size="middle">
          <Search
            placeholder="Search products by code, name, description, category, or notification group..."
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={handleSearch}
            value={searchText}
            style={{ width: 300 }}
          />
          <Select
            defaultValue="active"
            size="large"
            style={{ width: 150 }}
            onChange={handleFilterStatusChange}
            value={filterStatus}
          >
            <Option value="active">Active</Option>
            <Option value="inactive">Inactive</Option>
            <Option value="all">All Status (Incl. Deleted)</Option>
          </Select>
            {productType !== 'euc' && (
            <Select
              defaultValue="all"
              size="large"
              style={{ width: 200 }}
              onChange={handleFilterCategoryChange}
              loading={!productCategories.length && loading}
              value={filterCategory}
              showSearch
              filterOption={(input, option) =>
                option.children && option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              <Option value="all">All Categories</Option>
              {productCategories.map(category => (
                <Option key={category.id} value={category.id}>
                  {category.name}
                </Option>
              ))}
            </Select>
          )}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddProduct}
            size="large"
          >
            Add Product
          </Button>
        </Space>
      </div>
    </div>

      <Table
        columns={columns}
        dataSource={products}
        rowKey="id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: true }}
      />

      {/* Pass productCategories to ProductForm */}
      <ProductForm
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSuccess={() => {
          setIsModalVisible(false);
          setPagination(prev => ({ ...prev, current: 1 }));
          fetchProducts({ ...pagination, current: 1 }, sortBy, true); // Triggered by action
        }}
        product={editingProduct}
        productCategories={productCategories}
        defaultCategoryCode={
          productType === 'euc'
            ? 'EUC'
            : productType === 'enterprise'
            ? '!EUC'   // special marker: any non-EUC category
            : null
        }
      />
    </div>
  );
};

export default Products;